import { useCallback, useEffect, useRef } from 'react';

import { useBizyMarkNotificationsSeenMutation, useBizyUnreadNotifications } from '../../../queries/bizy-notifications';
import { markBizyActivity } from '../lib/bizy-activity-stamp';
import { useBizySidePanelStore } from '../store/use-bizy-side-panel-store';
import { useHasBizyNotification, useIsUnreadBizyObservedMoment, useUnreadBizyConversationId, useUnreadBizyPersonId } from './use-has-bizy-notification';
import { usePeopleBrowserEnabled } from './use-people-browser-enabled';

interface Options {
	/**
	 * Imperative callback that swaps the chat panel to the given conversation
	 * id. Wired to `useBizySidePanelChat().loadConversation` from the caller.
	 */
	loadConversation: (conversationId: string) => void;
	/**
	 * Optional secondary auto-load condition. The hook always auto-loads on
	 * `lastTrigger === 'notification_peek'`. Pass true to ALSO auto-load on
	 * other open paths (e.g., the panel is in empty state after a page
	 * refresh — you want the proactive conversation to land instead of an
	 * empty greeting). Pass false (default) to leave non-peek opens alone.
	 */
	autoLoadOnOpen?: boolean;
}

/**
 * Coordinates the proactive-notification UX inside the side panel.
 *
 * Behavior:
 *   - When the user opens the panel via the peek (`lastTrigger === 'notification_peek'`),
 *     the peek is the explicit acknowledgement, so we auto-load the
 *     conversation. The user lands directly on Bizy's message.
 *   - For any other open path (toggle, already-open + new notification), we
 *     leave the chat in its current state and let the empty-state greeting
 *     do its job. The user clicks the wave to engage, which loads the
 *     conversation.
 *
 * The panel renders one rolling timeline, so there's no per-conversation swap
 * to wait for: engagement loads the newest turn (if any) and marks the
 * entire unread backlog seen immediately via `markAllUnreadSeen`.
 */
export function useLoadProactiveConversationOnOpen({ loadConversation, autoLoadOnOpen = false }: Options) {
	const isOpen = useBizySidePanelStore((s) => s.isOpen);
	const lastTrigger = useBizySidePanelStore((s) => s.lastTrigger);
	const engageRequestVersion = useBizySidePanelStore((s) => s.engageRequestVersion);
	const hasUnread = useHasBizyNotification();
	const conversationId = useUnreadBizyConversationId();
	// Post-BIZY-633, the latest unread can be a People Browser observed moment
	// with no conversation to open. In that case we focus the sidebar entry
	// instead of attempting to load a nonexistent conversation.
	//
	// Only treat it as an observed moment when the People Browser is actually
	// enabled — if the backend `kind` goes live while the flags are off, the
	// sidebar isn't rendered, so focusing an entry would be a dead end and
	// leave the unread stuck. Falling through to the null-conversation
	// handling lets that clear it instead.
	const peopleBrowserEnabled = usePeopleBrowserEnabled();
	const isObservedMoment = useIsUnreadBizyObservedMoment() && peopleBrowserEnabled;
	const unreadPersonId = useUnreadBizyPersonId();
	const focusPerson = useBizySidePanelStore((s) => s.focusPerson);
	// `isFetching` lets us tell a transient null (a recent scoped markSeen
	// optimistically nulled latestConversationId and the refetch is in
	// flight) from a persistent null (legacy notification with no
	// chat_history_id linkage). The mark-seen mutation's onSuccess does
	// setQueryData → invalidateQueries synchronously, so the moment
	// observers see latestConversationId === null from the optimistic
	// update they also see isFetching === true.
	const { isFetching: isFetchingUnread } = useBizyUnreadNotifications();
	const { mutate: markSeen } = useBizyMarkNotificationsSeenMutation();

	// Snapshot of autoLoadOnOpen captured at the close→open transition.
	// We auto-load only if the snapshot is true, not the live prop. That
	// way a mid-session flip (e.g. user clicks "New chat" while an unread
	// is pending → isEmptyState becomes true → autoLoadOnOpen prop flips
	// to true) can't re-trigger an auto-load against the user's intent.
	// We also CAN'T just key off "first effect run with isOpen=true",
	// because hasUnread is async and usually resolves on a later effect
	// run — so the snapshot has to outlive multiple effect passes within
	// the same open session.
	const autoLoadOnOpenSnapshotRef = useRef(false);
	// Whether any explicit engagement (peek-engage, engageWithGreeting,
	// acknowledgeUnread) has happened this open session. Distinct from
	// `passiveAutoLoadFiredThisOpenRef` because an engagement-failure
	// rollback should NOT also un-fire a passive load that has already
	// swapped the chat view — that event is observable to the user and
	// can't be unwound.
	const engagementSuppressesPassiveRef = useRef(false);
	// Per-session dedup for `markAllUnreadSeen`. Without this, a burst of
	// engagement signals (e.g. one acknowledgeUnread per keystroke) would
	// fire one /seen request per call.
	const markAllFiredThisSessionRef = useRef(false);
	// Same pattern for lastTrigger. The store's lastTrigger sticks at
	// 'notification_peek' for the rest of the session after a peek-open;
	// without snapshotting, a non-peek session that flipped lastTrigger
	// for any reason would inherit peek semantics. We only auto-engage
	// when the session was actually peek-opened.
	const peekOpenedSnapshotRef = useRef(false);
	// One-shot guard: once the peek branch actually engages this open
	// session, it never engages again — even if a new notification
	// surfaces via window-focus refetch and changes `conversationId`.
	// Without this, the user would get yanked out of the conversation
	// they were reading. Subsequent unread notifications follow the same
	// in-chat-engage-by-click semantics as toggle-opened sessions.
	const peekFiredThisOpenRef = useRef(false);
	const wasOpenRef = useRef(false);
	// One-shot guard for the passive `autoLoadOnOpen` branch only.
	// Active engagement (peek, engageWithGreeting, acknowledgeUnread) uses
	// `engagementSuppressesPassiveRef` so a NEW notification mid-session can
	// still fire markSeen for its own conversation id.
	const passiveAutoLoadFiredThisOpenRef = useRef(false);

	// Per-conversation engagement guard. The peek branch (main effect) and the
	// engageRequestVersion effect below can both react to the same
	// `openFromNotificationPeek()` call in the same commit; without this, both
	// would call `engageWithConversation` for the same target and double-load
	// + double-clear. A NEW notification arriving mid-session (different
	// conversationId) still engages normally since it's not yet in the set.
	const engagedConversationsRef = useRef<Set<string>>(new Set());

	// Stable ref so the main effect's dep array reflects only the logical
	// inputs (open state, trigger, unread, conversation id) — not the
	// identity of the caller-provided `loadConversation` callback. If a
	// future caller ever passes a non-stable `loadConversation`, the effect
	// won't spuriously re-fire on every render. We always read `.current` so
	// behavior tracks the latest prop.
	const loadConversationRef = useRef(loadConversation);
	loadConversationRef.current = loadConversation;

	// Reset session-scoped state on close so the next open with a new
	// notification can engage fresh.
	useEffect(() => {
		if (!isOpen) {
			engagedConversationsRef.current = new Set();
			engagementSuppressesPassiveRef.current = false;
			markAllFiredThisSessionRef.current = false;
		}
	}, [isOpen]);

	// The panel renders one rolling timeline: every proactive turn — a whole
	// week's backlog after a long absence — lands in the same place, and
	// there's no per-conversation navigation to clear them one at a time. So
	// ANY engagement (peek, waving-Bizy click, typing) clears the ENTIRE
	// unread backlog via the backend "clear all" path (markSeen with no
	// conversation_id). Deduped + rolled back per open session.
	const markAllUnreadSeen = useCallback(() => {
		if (markAllFiredThisSessionRef.current) return;
		markAllFiredThisSessionRef.current = true;
		engagementSuppressesPassiveRef.current = true;
		markSeen(
			{},
			{
				onError: () => {
					markAllFiredThisSessionRef.current = false;
					engagementSuppressesPassiveRef.current = false;
				},
			}
		);
	}, [markSeen]);

	const engageWithConversation = useCallback(
		(targetId: string | null) => {
			// Every caller of this method is a deliberate gesture (peek click,
			// Ask-Bizy click, waving-mascot click) — that's conversation activity
			// for the idle blank slate. The passive auto-load branch below calls
			// loadConversation directly and deliberately does NOT stamp.
			markBizyActivity();
			// Guards only the redundant load: the peek branch and the
			// engageRequestVersion effect can both fire for the same
			// conversation from the same `openFromNotificationPeek()` call.
			// It must NOT gate `markAllUnreadSeen()` below — that call has its
			// own per-session dedup (with its own failure rollback), and a
			// user retrying a failed /seen request for an already-loaded
			// conversation still needs to reach it.
			if (targetId && !engagedConversationsRef.current.has(targetId)) {
				engagedConversationsRef.current.add(targetId);
				// No conversation swap will ever happen (the panel merges
				// proactive turns into one timeline instead of switching
				// conversations), so refresh the timeline to surface the
				// newest turn.
				loadConversationRef.current(targetId);
			}
			// Clear the whole backlog directly — deduped per session inside
			// markAllUnreadSeen.
			markAllUnreadSeen();
		},
		[markAllUnreadSeen]
	);

	useEffect(() => {
		if (!isOpen) {
			wasOpenRef.current = false;
			autoLoadOnOpenSnapshotRef.current = false;
			peekOpenedSnapshotRef.current = false;
			peekFiredThisOpenRef.current = false;
			passiveAutoLoadFiredThisOpenRef.current = false;
			return;
		}

		// Capture conditions at the moment the panel transitions to open.
		// Subsequent effect runs within the same open session use the
		// snapshot, not the live prop.
		if (!wasOpenRef.current) {
			wasOpenRef.current = true;
			autoLoadOnOpenSnapshotRef.current = autoLoadOnOpen;
			peekOpenedSnapshotRef.current = lastTrigger === 'notification_peek';
		}

		if (!hasUnread) return;

		// Two auto-paths, with different acknowledgement semantics:
		//   - peek-opened snapshot: explicit click on the unread indicator.
		//     Load AND mark seen — the user told us they saw it. Fires at
		//     most once per open session: a new notification surfacing
		//     mid-session via window-focus refetch must NOT yank the user
		//     out of whatever they were reading. The transient-vs-
		//     persistent null disambiguation lives inside
		//     engageWithConversation; we set the fire guard before calling
		//     so transient null + later resolution still engages cleanly
		//     (engageWithConversation no-ops on null + isFetching, so the
		//     guard release in that path lets the next effect run try
		//     again — see below).
		//   - autoLoadOnOpen snapshot (refresh-into-empty-state): passive.
		//     Load the conversation so the user lands on Bizy's message, but
		//     DO NOT mark seen. The in-chat waving Bizy will dance until the
		//     user actually engages (hover stops the dance; click/type marks
		//     seen). One-shot per open session, and only when there's a
		//     conversation to load (legacy notifications have nothing to
		//     surface — the user can clear them via engageWithGreeting).
		if (peekOpenedSnapshotRef.current && !peekFiredThisOpenRef.current) {
			// Observed-moment peek: there is no conversation to load — open the
			// People Browser and focus the entry instead. Marking the moment
			// seen happens when the user interacts with the sidebar entry (it
			// uses the notification-scoped people seen mutation), so we don't
			// fire markAllUnreadSeen here.
			if (isObservedMoment) {
				peekFiredThisOpenRef.current = true;
				focusPerson(unreadPersonId);
				return;
			}
			// Engage if there's something to engage with. For transient null
			// (refetch in flight) we leave the guard false so the effect
			// re-runs when conversationId or isFetchingUnread resolves. For
			// persistent null (legacy notification) engageWithConversation
			// falls back to markAllUnreadSeen — count that as the peek branch
			// firing. Read isFetchingUnread directly (not via ref) so its
			// true→false transition triggers a re-run of this effect; without
			// that, a refetch landing while conversationId stayed null (e.g.
			// the remaining unread is also legacy) would leave the indicator
			// stuck until panel close.
			if (conversationId) {
				peekFiredThisOpenRef.current = true;
				engageWithConversation(conversationId);
			} else if (!isFetchingUnread) {
				peekFiredThisOpenRef.current = true;
				engageWithConversation(null);
			}
			return;
		}
		if (autoLoadOnOpenSnapshotRef.current && !passiveAutoLoadFiredThisOpenRef.current && !engagementSuppressesPassiveRef.current && conversationId) {
			passiveAutoLoadFiredThisOpenRef.current = true;
			// Passive: surfaces Bizy's message without marking the proactive
			// notification seen (the waving Bizy still dances until the user
			// engages). loadConversation stamps activity when it merges a
			// genuinely new proactive turn (BIZY-692) so the idle blank slate
			// can't arm on top of Bizy's greeting.
			loadConversationRef.current(conversationId);
		}
	}, [isOpen, lastTrigger, autoLoadOnOpen, hasUnread, conversationId, isFetchingUnread, engageWithConversation, isObservedMoment, unreadPersonId, focusPerson]);

	// Click-driven engagement signal: bumped by the store when the user
	// clicks Ask Bizy with an unread, regardless of whether the panel was
	// already open. The peek branch above only fires once per close→open
	// transition (snapshot pattern), so a user who Ask-Bizy-clicks while
	// the panel is already open would otherwise see nothing — they'd have
	// to open the dropdown and pick the conversation manually. This
	// effect closes that gap. We dedupe on the version so the handler
	// only runs on actual bumps, not on stale renders.
	const lastEngageRequestRef = useRef(engageRequestVersion);
	useEffect(() => {
		if (engageRequestVersion === lastEngageRequestRef.current) return;
		lastEngageRequestRef.current = engageRequestVersion;
		if (!hasUnread) return;
		// Observed-moment unread: focus the People Browser entry rather than
		// engaging a (nonexistent) conversation.
		if (isObservedMoment) {
			focusPerson(unreadPersonId);
			return;
		}
		engageWithConversation(conversationId);
	}, [engageRequestVersion, hasUnread, conversationId, engageWithConversation, isObservedMoment, unreadPersonId, focusPerson]);

	const engageWithGreeting = useCallback(() => {
		if (!hasUnread) return;
		// Observed-moment unread: the waving Bizy click should focus the People
		// Browser entry, not engage a (nonexistent) conversation.
		if (isObservedMoment) {
			focusPerson(unreadPersonId);
			return;
		}
		engageWithConversation(conversationId);
	}, [hasUnread, isObservedMoment, unreadPersonId, focusPerson, conversationId, engageWithConversation]);

	// Lighter-weight engagement signal: marks the unread proactive
	// notification(s) as seen WITHOUT loading a conversation. Use this when
	// the user has clearly engaged with Bizy in some other way (typed in
	// the input, clicked the Ask Bizy button) and they don't need the
	// proactive conversation switched in front of whatever they were doing.
	//
	// Deduped per session via `markAllFiredThisSessionRef` so callers like
	// the chat input's onChange (which fires per keystroke) only produce one
	// markSeen request per session.
	const acknowledgeUnread = useCallback(() => {
		if (!hasUnread) return;
		// Observed-moment unread has no conversation — don't fire the
		// markAllUnreadSeen fallback. Its seen/skip state is managed by the
		// sidebar entry's own mutations (people seen/skip), so a generic
		// chat-input ack is a no-op here.
		if (isObservedMoment) return;
		markAllUnreadSeen();
	}, [hasUnread, isObservedMoment, markAllUnreadSeen]);

	return {
		showGreeting: hasUnread,
		engageWithGreeting,
		acknowledgeUnread,
	};
}
